import type { UserTeam } from '@skop-app/lib-domain/types'
import type { SeatAwareUserTeam } from '@/types/seatAccess'
import { hasEffectiveSeatAccess } from '@/utils/seatAccess'

export const isPersonalWorkspaceMembership = (
  team: UserTeam | null | undefined,
) => team?.status === 'DONE' && team.organizationStatus === 'SOLO'

export const isSelectableTeamMembership = (
  team: SeatAwareUserTeam | null | undefined,
) =>
  team?.status === 'DONE' &&
  team.organizationStatus !== 'SOLO' &&
  hasEffectiveSeatAccess(team)

export const isAcceptedOrganizationMembership = (
  team: Pick<UserTeam, 'status'> | null | undefined,
) => team?.status === 'DONE'

export const getSelectableTeams = (
  teams: ReadonlyArray<UserTeam> | undefined,
) => teams?.filter(isSelectableTeamMembership) ?? []

export const getPersonalWorkspaceMembership = (
  teams: ReadonlyArray<UserTeam> | undefined,
) => {
  const workspaces = teams?.filter(isPersonalWorkspaceMembership) ?? []
  return workspaces.length === 1 ? workspaces[0] : undefined
}

/**
 * Resolve the active organization id using a clear precedence:
 * 0) if cookie-selected id is 'solo', return null only while a personal workspace still exists
 * 1) cookie-selected id (if present and valid for a selectable team)
 * 2) preferred id from user preferences (accepted personal workspace or team)
 * 3) first selectable team with status DONE
 * 4) null for solo/personal workspace or when no accepted team exists yet
 */
export function resolveActiveTeamId(
  teams: ReadonlyArray<UserTeam> | undefined,
  cookieSelectedId: string | null | undefined,
  preferredId: string | undefined,
): string | null {
  const selectableTeams = getSelectableTeams(teams)
  const personalWorkspace = getPersonalWorkspaceMembership(teams)

  const hasSelectableOrganization = (
    id: string | null | undefined,
  ): id is string =>
    Boolean(id && selectableTeams.some((team) => team.value === id))

  if (cookieSelectedId === 'solo') {
    if (personalWorkspace) return null
  } else if (hasSelectableOrganization(cookieSelectedId)) {
    return cookieSelectedId
  }

  if (
    preferredId === 'solo' ||
    (preferredId && personalWorkspace?.value === preferredId)
  ) {
    if (personalWorkspace) return null
  } else if (hasSelectableOrganization(preferredId)) {
    return preferredId
  }

  return selectableTeams[0]?.value ?? null
}
